import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/authStore';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

export function LoginPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { loginWithEmail, signUpWithEmail, signInWithApple, isLoading, googleButtonRef } =
    useAuth();

  if (isAuthenticated) {
    return <Navigate to="/closet" replace />;
  }

  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    email: '',
    password: '',
    displayName: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSignUp) {
      await signUpWithEmail({
        email: form.email,
        password: form.password,
        displayName: form.displayName,
      });
    } else {
      await loginWithEmail({
        email: form.email,
        password: form.password,
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-vestir-purple/5 to-vestir-white
                    flex flex-col items-center justify-center px-6 py-12">
      {/* Logo & Brand */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-10"
      >
        <div className="w-16 h-16 bg-vestir-purple rounded-2xl flex items-center justify-center
                       mx-auto mb-4 shadow-lg">
          <span className="text-white font-display text-2xl font-bold">V</span>
        </div>
        <h1 className="font-display text-3xl font-bold text-vestir-black">
          Vestir
        </h1>
        <p className="text-vestir-gray-dark mt-2 text-sm">
          Your virtual wardrobe, reimagined
        </p>
      </motion.div>

      {/* Form Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="w-full max-w-sm"
      >
        <div className="bg-white rounded-3xl shadow-card p-6 space-y-5">
          <h2 className="font-display text-xl font-semibold text-center">
            {isSignUp ? 'Create Account' : 'Welcome Back'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Display Name (sign up only) */}
            {isSignUp && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <label htmlFor="displayName" className="sr-only">
                  Display name
                </label>
                <div className="relative">
                  <User
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-vestir-gray-dark"
                  />
                  <input
                    id="displayName"
                    type="text"
                    placeholder="Display name"
                    value={form.displayName}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, displayName: e.target.value }))
                    }
                    className="input-field pl-10"
                    required={isSignUp}
                    autoComplete="name"
                  />
                </div>
              </motion.div>
            )}

            {/* Email */}
            <div>
              <label htmlFor="email" className="sr-only">
                Email
              </label>
              <div className="relative">
                <Mail
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-vestir-gray-dark"
                />
                <input
                  id="email"
                  type="email"
                  placeholder="Email address"
                  value={form.email}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, email: e.target.value }))
                  }
                  className="input-field pl-10"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <div className="relative">
                <Lock
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-vestir-gray-dark"
                />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={form.password}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, password: e.target.value }))
                  }
                  className="input-field pl-10 pr-10"
                  required
                  minLength={8}
                  autoComplete={isSignUp ? 'new-password' : 'current-password'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-vestir-gray-dark"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  {isSignUp ? 'Create Account' : 'Sign In'}
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-vestir-gray" />
            <span className="text-xs text-vestir-gray-dark">or continue with</span>
            <div className="flex-1 h-px bg-vestir-gray" />
          </div>

          {/* Social Sign-In */}
          <div className="space-y-3">
            {/* Google Sign-In container */}
            <div
              ref={googleButtonRef}
              className="flex justify-center"
              aria-label="Sign in with Google"
            />

            {/* Apple Sign-In */}
            <button
              onClick={signInWithApple}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-3 px-4 py-3
                        bg-black text-white rounded-2xl font-medium text-sm
                        hover:bg-gray-900 transition-colors"
              aria-label="Sign in with Apple"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.52-3.23 0-1.44.65-2.2.46-3.06-.4C3.79 16.17 4.36 9.02 8.8 8.76c1.24.07 2.1.72 2.83.78.98-.2 1.92-.78 2.98-.7 1.26.1 2.2.58 2.82 1.5-2.58 1.55-1.97 4.96.45 5.92-.55 1.44-1.26 2.86-2.83 4.02zM12.05 8.68c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              Continue with Apple
            </button>
          </div>

          {/* Toggle Sign In / Sign Up */}
          <p className="text-center text-sm text-vestir-gray-dark">
            {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-vestir-purple font-medium hover:underline"
            >
              {isSignUp ? 'Sign In' : 'Sign Up'}
            </button>
          </p>
        </div>

        {/* Terms */}
        <p className="text-[11px] text-vestir-gray-dark text-center mt-4 px-4">
          By continuing, you agree to Vestir's{' '}
          <a href="/terms" className="underline">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="/privacy" className="underline">
            Privacy Policy
          </a>
        </p>
      </motion.div>
    </div>
  );
}
